import paper from "paper";
import { isVisible, type Piece, type StitchLine } from "./Project";
import { stitchHolePlacements, type StitchHolePlacement } from "./stitching";

/**
 * The single source of truth for WHICH stitch holes a seam actually keeps
 * (#910). It composes the two independent retention layers into one answer:
 *
 *  - Layer A — the work-area WINDOW: `stitchHolePlacements(line)` already
 *    truncates the row to the seam's `[startInset, length - endInset]` window.
 *  - Layer B — the LEATHER: a placement is kept only when its hole CENTER sits
 *    on the piece's leather retention region (the outline MINUS the visible
 *    holes MINUS the cutouts), so holes that overflow the piece edge or land in
 *    a punch-hole / cutout are dropped.
 *
 * PR 1 of #910 only BUILDS this (and the shared region helper); it is not yet
 * wired into rendering, the exporters, the panel count, instructions, or the
 * e2e accessor — those migrate in later PRs. Kept in its OWN paper-using module
 * (like `stitchCrop.ts`) so the paper-free `stitching.ts` stays canvas-free.
 */

/** How far off the retention region a hole center may sit and still count as on
 *  the leather — the same on-outline tolerance used by `stitchCrop.ts` (L19) and
 *  `constructionOutline.ts` (L543). A hole exactly on the edge (float noise on a
 *  boundary sample) is treated as inside, so it isn't flicker-dropped. */
const ON_OUTLINE_TOL_MM = 0.5;

/**
 * Build a piece's LEATHER RETENTION REGION in ORIGINAL (construction) coordinate
 * space: `shapePathData` MINUS its visible holes MINUS its visible cutouts. This
 * is the exact region `pieceRenderer.buildLeatherClip` clips decorations to, and
 * the region `placedStitchHoles` tests hole centers against — extracted here so
 * both share one identical definition (#910).
 *
 * Every temporary path is constructed with `insert: false` so nothing leaks into
 * the project tree; the returned region is DETACHED and owned by the caller,
 * which must `.remove()` it when done. `pieceDisplayOffset` is intentionally NOT
 * applied — retention is computed in original space, which the seam points and
 * `shapePathData` already share. Returns null when `shapePathData` won't parse.
 */
export function buildLeatherRegion(
  piece: Piece,
  scope: paper.PaperScope,
): paper.PathItem | null {
  let region: paper.PathItem;
  try {
    region = new scope.CompoundPath({
      pathData: piece.shapePathData,
      insert: false,
    });
  } catch {
    return null;
  }

  for (const hole of piece.holes) {
    if (!isVisible(hole)) continue;
    const c = new scope.Path.Circle({
      center: new scope.Point(hole.position.x, hole.position.y),
      radius: hole.radius,
      insert: false,
    });
    const next = region.subtract(c, { insert: false });
    region.remove();
    region = next;
  }
  for (const cut of piece.cutouts) {
    if (!isVisible(cut)) continue;
    try {
      const cp = new scope.CompoundPath({
        pathData: cut.pathData,
        insert: false,
      });
      const next = region.subtract(cp, { insert: false });
      region.remove();
      cp.remove();
      region = next;
    } catch {
      /* ignore malformed cutout */
    }
  }
  // Stitching holes are intentionally NOT subtracted — they sit on top of the
  // leather as marks rather than cutouts.
  return region;
}

/**
 * Exactly the placements a seam should KEEP: those both within the work-area
 * window AND on the leather (#910). Layer A (`stitchHolePlacements`) runs first,
 * then survivors are filtered to hole centers on the retention region. The
 * `conforming` / `skip` flags are carried through unchanged (the surviving
 * placement objects are returned as-is), and filtering happens BEFORE any marks
 * are built, so a later consumer can pass these straight into
 * `marksFromPlacements` without re-running Layer A.
 *
 * When the outline won't parse, no holes are dropped (the seam is returned
 * whole) — the same fail-open contract `stitchCrop.ts` uses for an unparseable
 * outline.
 */
export function placedStitchHoles(
  line: StitchLine,
  piece: Piece,
  scope: paper.PaperScope,
): StitchHolePlacement[] {
  const placements = stitchHolePlacements(line);
  if (placements.length === 0) return placements;

  const region = buildLeatherRegion(piece, scope);
  if (!region) return placements; // unparseable outline → drop nothing

  const onLeather = (p: { x: number; y: number }): boolean => {
    const pt = new scope.Point(p.x, p.y);
    // On-boundary counts as inside: `contains` is false exactly on the edge, so
    // fall back to the nearest-point distance and treat within-tolerance as in.
    if (region.contains(pt)) return true;
    const near = region.getNearestLocation(pt);
    return (near?.point.getDistance(pt) ?? Infinity) <= ON_OUTLINE_TOL_MM;
  };

  const kept = placements.filter((pl) => onLeather(pl.position));
  region.remove();
  return kept;
}
